import { useDocumentsWorker, WorkerState } from '@Contexts/documents';
import { type BlobInput } from '@oicana/browser';
import { LoadingFrame, useLogger, useToasts } from '@synqo/frontend-component-library';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import classes from './TemplatePreview.module.css';
import { useCompiledTemplate } from './useCompiledTemplate';
import { usePageImages } from './usePageImages';
import { usePageVisibility } from './usePageVisibility';

// Resolution handed to the worker when rendering a page, in pixels per typographic point.
const PIXELS_PER_PT = 1.2;

interface ITemplatePreviewProps {
    printOptionId: string;
    blobDatasets: Map<string, BlobInput>;
    jsonDatasets: Map<string, string>;
}

// Shown when the documents worker itself failed to start (e.g. the WASM engine couldn't
// initialize). Compiling anything is hopeless in that state, so we don't even mount the compile
// pipeline below — this replaces it with a single, stable message instead of a failing spinner.
// @ai-generated 2026-07-23 by matthias.gessinger@synqony.com using Claude Opus 4.8
function PreviewUnavailable() {
    return (
        <div className={classes.previewUnavailable}>
            <IconAlertTriangle size={20} />
            <span>Die Vorschau ist derzeit nicht verfügbar.</span>
        </div>
    );
}

export function TemplatePreview(props: ITemplatePreviewProps) {
    const { state } = useDocumentsWorker();

    // Gate the whole compile/render pipeline on the worker being usable. Rendering the fallback
    // instead of TemplatePreviewContent means its hooks never run, so a broken worker produces one
    // calm message rather than a failed compile (and error toast) per document on screen.
    if (state === WorkerState.Broken) {
        return <PreviewUnavailable />;
    }

    return <TemplatePreviewContent {...props} />;
}

function TemplatePreviewContent({ printOptionId, jsonDatasets, blobDatasets }: ITemplatePreviewProps) {
    const [hasError, setHasError] = useState(false);
    const [failedPages, setFailedPages] = useState<Set<number>>(new Set());
    const { pushToast } = useToasts();
    const { warn } = useLogger();
    const containerRef = useRef<HTMLDivElement>(null);

    const showLoadErrorToast = useCallback(
        (e: unknown) => {
            pushToast({ message: 'Die Vorschau konnte nicht geladen werden.', variant: 'error' });
            if (e instanceof Error) {
                warn(e.message);
            }
        },
        [pushToast, warn],
    );

    // The whole document failed to compile: there is nothing to show at all.
    // @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
    const reportError = useCallback(
        (e: unknown) => {
            setHasError(true);
            showLoadErrorToast(e);
        },
        [showLoadErrorToast],
    );

    // Only one page failed to render: the rest of the document is still shown, so just mark that
    // single page as failed instead of blanking the entire preview.
    // @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
    const reportPageError = useCallback(
        (pageIndex: number, e: unknown) => {
            setFailedPages((current) => {
                const next = new Set(current);
                next.add(pageIndex);
                return next;
            });
            showLoadErrorToast(e);
        },
        [showLoadErrorToast],
    );

    useEffect(() => {
        setHasError(false);
        setFailedPages(new Set());
    }, [printOptionId, jsonDatasets, blobDatasets]);

    const { documentToken, pages } = useCompiledTemplate(printOptionId, jsonDatasets, blobDatasets, reportError);
    const { pageImages, requestPage } = usePageImages(documentToken, PIXELS_PER_PT, reportPageError);

    usePageVisibility(containerRef, pages.length, requestPage);

    if (hasError) {
        return null;
    }

    if (pages.length === 0) {
        return <LoadingFrame />;
    }

    return (
        <div ref={containerRef} className={classes.pageColumn}>
            {pages.map((page, index) => {
                const imageUrl = pageImages.get(index);
                const failed = failedPages.has(index);
                return (
                    <div key={index} data-page-index={index}>
                        {!failed && imageUrl ? (
                            <img
                                className={classes.template}
                                src={imageUrl}
                                alt={`Preview for '${printOptionId}', page ${index + 1}`}
                            />
                        ) : (
                            <div
                                className={classes.pagePlaceholder}
                                style={{ aspectRatio: `${page.width} / ${page.height}` }}
                            >
                                {!failed && <LoadingFrame />}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
